import { useEffect, useMemo, useState } from "react";
import { Link } from "@/lib/router-compat";
import PageMeta from "@/components/PageMeta";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { AlertTriangle, ArrowRight, CheckCircle2, ShieldCheck } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import RelatedTools from "@/components/tools/RelatedTools";
import {
  BUY_BEFORE_SELLING_FAQS,
  COMPARISON_FACTORS,
  EXPERT_REVIEW,
  MOVE_UP_OPTIONS,
  MOVE_UP_TIMELINE,
} from "@/data/buyBeforeSellingData";

const PAGE_URL = "https://www.homeapproach.com/tools/buy-before-selling";
const PAGE_TITLE = "How to Buy a New Home Before Selling Your Current One";

const CONTENTS = [
  { id: "compare", label: "Compare every option" },
  { id: "bridge-vs-heloc", label: "Bridge loan vs HELOC" },
  { id: "retirement", label: "401(k) and retirement loans" },
  { id: "contingency", label: "Sale contingency vs clean offer" },
  { id: "rent-back", label: "Rent-back and extended closing" },
  { id: "rental", label: "Keeping the home as a rental" },
  { id: "programs", label: "Buy-before-you-sell programs" },
  { id: "matrix", label: "Which option fits you" },
  { id: "timeline", label: "30-60-90 day timeline" },
  { id: "faq", label: "Common questions" },
];

const opt = (id: string) => MOVE_UP_OPTIONS.find((o) => o.id === id)!;

type Answers = {
  equity: "low" | "some" | "lots";
  carry: "no" | "tight" | "yes";
  market: "slow" | "balanced" | "competitive";
  flexibility: "rigid" | "flexible";
};

function recommend(a: Answers) {
  const scores: Record<string, number> = {};
  const reasons: Record<string, string[]> = {};
  const add = (id: string, points: number, why: string) => {
    scores[id] = (scores[id] ?? 0) + points;
    (reasons[id] ??= []).push(why);
  };

  MOVE_UP_OPTIONS.forEach((o) => add(o.id, 0, ""));

  if (a.equity === "lots") {
    add("heloc", 3, "You have meaningful equity, which is what a line of credit is sized against.");
    add("bridge-loan", 2, "Bridge loans are sized against the equity in the departing home.");
  } else if (a.equity === "some") {
    add("bridge-loan", 2, "A bridge can stretch partial equity far enough to cover a down payment.");
    add("401k-loan", 2, "A plan loan can cover a smaller gap that equity alone does not close.");
  } else {
    add("sale-contingency", 3, "With little equity there is not much to borrow against yet.");
    add("rent-back", 2, "Selling first turns whatever equity you do have into usable cash.");
    add("401k-loan", 1, "A plan loan is the one source here that does not depend on home equity.");
  }

  if (a.carry === "yes") {
    add("bridge-loan", 2, "You can absorb both payments if the sale takes longer than planned.");
    add("keep-as-rental", 3, "Carrying capacity is the main requirement for keeping the old home.");
    add("heloc", 1, "Line payments stack on top of both mortgages, which you can cover.");
  } else if (a.carry === "tight") {
    add("rent-back", 2, "Selling first removes the double-payment exposure entirely.");
    add("extended-closing", 2, "A longer escrow can let the sale fund the purchase with no overlap.");
    add("keep-as-rental", -3, "Two ongoing payments with vacancy risk is a poor fit for a tight budget.");
  } else {
    add("sale-contingency", 3, "You should not be exposed to two payments at all.");
    add("rent-back", 3, "Sell-first plus a rent-back keeps you to one payment and one move.");
    add("bridge-loan", -3, "A bridge assumes you can carry both homes while the old one sells.");
    add("keep-as-rental", -4, "Landlording requires reserves for vacancy and repairs.");
  }

  if (a.market === "competitive") {
    add("bridge-loan", 2, "A non-contingent offer is often the price of entry in a hot market.");
    add("buy-before-you-sell", 3, "These programs exist specifically to produce a clean, strong offer.");
    add("sale-contingency", -4, "Contingent offers usually lose to clean ones at the same price here.");
  } else if (a.market === "balanced") {
    add("sale-contingency", 1, "Contingencies are still accepted in a balanced market.");
    add("extended-closing", 1, "Sellers here are often willing to negotiate the closing date.");
  } else {
    add("sale-contingency", 3, "In a slow market a contingency costs you little and protects you.");
    add("extended-closing", 2, "Slow markets give you room to negotiate a longer escrow.");
    add("buy-before-you-sell", -2, "Program fees buy speed you may not need in this market.");
  }

  if (a.flexibility === "flexible") {
    add("rent-back", 2, "You can absorb a gap between closings without breaking anything.");
    add("extended-closing", 2, "A flexible move date is what makes a long escrow workable.");
  } else {
    add("bridge-loan", 1, "A hard move date rewards structures that let you move once.");
    add("buy-before-you-sell", 1, "Programs are built around moving once on a fixed date.");
    add("401k-loan", 1, "Plan loans typically fund faster than any other option here.");
    add("rent-back", -2, "A rent-back adds schedule dependency you cannot absorb.");
  }

  return Object.entries(scores)
    .map(([id, score]) => ({ id, score, reasons: (reasons[id] ?? []).filter(Boolean) }))
    .sort((x, y) => y.score - x.score)
    .slice(0, 3);
}

const QUESTIONS: {
  key: keyof Answers;
  label: string;
  options: { value: string; label: string }[];
}[] = [
  {
    key: "equity",
    label: "How much usable equity is in your current home?",
    options: [
      { value: "low", label: "Little or none" },
      { value: "some", label: "Some" },
      { value: "lots", label: "A lot" },
    ],
  },
  {
    key: "carry",
    label: "Could you cover both housing payments for three months?",
    options: [
      { value: "no", label: "No" },
      { value: "tight", label: "Barely" },
      { value: "yes", label: "Comfortably" },
    ],
  },
  {
    key: "market",
    label: "How competitive is the market you're buying into?",
    options: [
      { value: "slow", label: "Slow" },
      { value: "balanced", label: "Balanced" },
      { value: "competitive", label: "Very competitive" },
    ],
  },
  {
    key: "flexibility",
    label: "How flexible is your move date?",
    options: [
      { value: "rigid", label: "Fixed date" },
      { value: "flexible", label: "Flexible" },
    ],
  },
];

const BuyBeforeSelling = () => {
  const [answers, setAnswers] = useState<Answers>({
    equity: "some",
    carry: "tight",
    market: "balanced",
    flexibility: "flexible",
  });
  const [openStages, setOpenStages] = useState<string[]>([MOVE_UP_TIMELINE[0].id]);
  const [openFaq, setOpenFaq] = useState<string>("");
  const [reviewOpen, setReviewOpen] = useState(false);

  useEffect(() => {
    trackEvent("guide_view", { section_name: "buy_before_selling", cta_position: "page_load" });
  }, []);

  const results = useMemo(() => recommend(answers), [answers]);

  const setAnswer = (key: keyof Answers, value: string) => {
    setAnswers((prev) => ({ ...prev, [key]: value }) as Answers);
    trackEvent("option_table_interaction", {
      section_name: "decision_matrix",
      option_type: `${key}:${value}`,
      cta_position: "matrix",
    });
  };

  const handleFaqChange = (value: string) => {
    setOpenFaq(value);
    if (value) {
      trackEvent("faq_toggle", { section_name: "faq", option_type: value, cta_position: "faq" });
    }
  };

  const handleCta = (position: string, optionType: string) =>
    trackEvent("guide_cta_click", {
      section_name: position,
      option_type: optionType,
      cta_position: position,
    });

  const handleReviewToggle = () => {
    const next = !reviewOpen;
    setReviewOpen(next);
    if (next) {
      trackEvent("expert_review_expand", {
        section_name: "expert_review",
        option_type: "reviewer",
        cta_position: "intro",
      });
    }
  };

  const handleTableInteraction = (optionType: string) =>
    trackEvent("option_table_interaction", {
      section_name: "comparison_table",
      option_type: optionType,
      cta_position: "compare",
    });

  const description =
    "Bridge loan, HELOC, 401(k) loan, sale contingency, rent-back, extended closing, or keep it as a rental — compare every way to buy a new home before selling your current one, with a decision matrix and a 30-60-90 day timeline.";

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: PAGE_TITLE,
      description,
      url: PAGE_URL,
      inLanguage: "en-US",
      isPartOf: { "@type": "WebSite", name: "Home Approach", url: "https://www.homeapproach.com" },
      publisher: {
        "@type": "Organization",
        name: "Home Approach",
        url: "https://www.homeapproach.com",
        logo: { "@type": "ImageObject", url: "https://www.homeapproach.com/og-image.png" },
      },
      reviewedBy: { "@type": "Organization", name: EXPERT_REVIEW.name },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://www.homeapproach.com/" },
        { "@type": "ListItem", position: 2, name: "Tools", item: "https://www.homeapproach.com/tools" },
        { "@type": "ListItem", position: 3, name: PAGE_TITLE, item: PAGE_URL },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: BUY_BEFORE_SELLING_FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  const OptionSection = ({
    id,
    heading,
    intro,
    ids,
    ctaLabel,
  }: {
    id: string;
    heading: string;
    intro: string;
    ids: string[];
    ctaLabel: string;
  }) => (
    <section id={id} className="mb-12 scroll-mt-24">
      <h2 className="mb-2 text-2xl font-bold text-foreground">{heading}</h2>
      <p className="mb-5 text-sm text-muted-foreground">{intro}</p>
      <div className="space-y-4">
        {ids.map((oid) => {
          const o = opt(oid);
          return (
            <Card key={o.id} className="border-l-4 border-l-primary">
              <CardContent className="p-5">
                <h3 className="mb-1 font-bold text-foreground">{o.name}</h3>
                <p className="mb-3 text-sm text-primary">{o.short}</p>
                <p className="mb-2 text-sm leading-relaxed text-muted-foreground">{o.how}</p>
                <p className="mb-2 text-sm leading-relaxed text-foreground">
                  <span className="font-semibold">Best for: </span>
                  {o.bestFor}
                </p>
                <p className="flex gap-2 text-sm leading-relaxed text-muted-foreground">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                  <span>{o.disclosure}</span>
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <Card className="mt-5 border-primary/30 bg-primary/5">
        <CardContent className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-foreground">{ctaLabel}</p>
          <Button asChild onClick={() => handleCta(id, ids.join("|"))}>
            <Link to="/homebuying-support">
              Talk through your options <ArrowRight className="ml-1 h-4 w-4" aria-hidden />
            </Link>
          </Button>
        </CardContent>
      </Card>
    </section>
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PageMeta title={PAGE_TITLE} description={description} path="/tools/buy-before-selling" jsonLd={jsonLd} />
      <Header />

      <main className="flex-1">
        <div className="container mx-auto max-w-4xl px-4 py-8 sm:py-12">
          <Card className="mb-6 border-primary/30 bg-primary/5">
            <CardContent className="p-5 sm:p-6">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-primary">Short answer</p>
              <p className="text-base leading-relaxed text-foreground sm:text-lg">
                Buying before selling comes down to one question: where does the down payment come from before your
                current home closes? Either you borrow against your equity (bridge loan, HELOC), borrow elsewhere
                (401(k) loan, buy-before-you-sell program), or restructure the timing so you never need to
                (sale contingency, rent-back, extended closing).
              </p>
            </CardContent>
          </Card>

          <h1 className="mb-4 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{PAGE_TITLE}</h1>
          <p className="mb-4 text-base text-muted-foreground sm:text-lg">
            Every way move-up buyers bridge the gap between two closings, compared on equity access, offer strength,
            double-payment risk, and move timing — plus a decision matrix and a 30-60-90 day plan.
          </p>

          <Card className="mb-6 border-border bg-muted/30">
            <CardContent className="p-4 sm:p-5">
              <button
                type="button"
                onClick={handleReviewToggle}
                aria-expanded={reviewOpen}
                className="flex w-full items-center gap-2 text-left text-sm font-semibold text-foreground"
              >
                <ShieldCheck className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                Reviewed by {EXPERT_REVIEW.name} &middot; {EXPERT_REVIEW.reviewDate}
                <span className="ml-auto text-xs font-normal text-primary">
                  {reviewOpen ? "Hide details" : "Review details"}
                </span>
              </button>
              {reviewOpen && (
                <div className="mt-3 border-t border-border pt-3 text-sm text-muted-foreground">
                  <p className="mb-1">
                    <span className="font-semibold text-foreground">{EXPERT_REVIEW.name}</span> &mdash;{" "}
                    {EXPERT_REVIEW.role}
                  </p>
                  <p>{EXPERT_REVIEW.note}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <nav
            aria-label="On this page"
            className="sticky top-16 z-20 mb-10 rounded-xl border border-border bg-card/95 p-5 shadow-sm backdrop-blur print:hidden"
          >
            <p className="mb-3 text-sm font-bold text-foreground">Jump to a section</p>
            <ul className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              {CONTENTS.map((c) => (
                <li key={c.id}>
                  <a href={`#${c.id}`} className="text-primary hover:underline">
                    {c.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <section id="compare" className="mb-12 scroll-mt-24">
            <h2 className="mb-2 text-2xl font-bold text-foreground">Compare every option</h2>
            <p className="mb-5 text-sm text-muted-foreground">
              The same five questions asked of all eight paths. Scroll sideways on a phone.
            </p>
            <div className="overflow-x-auto rounded-xl border border-border" onScroll={() => handleTableInteraction("scroll")}>
              <table className="w-full min-w-[52rem] border-collapse text-sm">
                <caption className="sr-only">
                  Comparison of ways to buy a new home before selling your current one
                </caption>
                <thead>
                  <tr className="bg-muted/50">
                    <th scope="col" className="p-3 text-left font-bold text-foreground">
                      Option
                    </th>
                    {COMPARISON_FACTORS.map((f) => (
                      <th key={f.key} scope="col" className="p-3 text-left font-bold text-foreground">
                        {f.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {MOVE_UP_OPTIONS.map((o) => (
                    <tr
                      key={o.id}
                      className="border-t border-border align-top hover:bg-muted/30"
                      onMouseEnter={() => handleTableInteraction(o.id)}
                    >
                      <th scope="row" className="p-3 text-left font-semibold text-foreground">
                        {o.name}
                      </th>
                      {COMPARISON_FACTORS.map((f) => (
                        <td key={f.key} className="p-3 text-muted-foreground">
                          {o[f.key]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <OptionSection
            id="bridge-vs-heloc"
            heading="Bridge loan vs HELOC"
            intro="Both borrow against the home you are leaving. The difference that decides it is usually timing, not price."
            ids={["bridge-loan", "heloc"]}
            ctaLabel="Not sure which of these your lender will actually approve?"
          />

          <OptionSection
            id="retirement"
            heading="401(k) and retirement loans"
            intro="The one source of down payment funds that does not depend on your home equity at all."
            ids={["401k-loan"]}
            ctaLabel="Want to see how a plan loan changes your qualifying numbers?"
          />

          <OptionSection
            id="contingency"
            heading="Sale contingency vs a clean offer"
            intro="The cheapest protection available, and in a competitive market often the reason your offer loses."
            ids={["sale-contingency"]}
            ctaLabel="Wondering whether contingent offers are being accepted in your target area?"
          />

          <OptionSection
            id="rent-back"
            heading="Rent-back and extended closing"
            intro="Two ways to change the calendar instead of borrowing money."
            ids={["rent-back", "extended-closing"]}
            ctaLabel="Need help lining up two closing dates that actually work?"
          />

          <OptionSection
            id="rental"
            heading="Keeping the current home as a rental"
            intro="Attractive when you hold a low fixed rate, demanding when you look at qualifying and vacancy."
            ids={["keep-as-rental"]}
            ctaLabel="Want to know whether your lender will count the rental income?"
          />

          <OptionSection
            id="programs"
            heading="Buy-before-you-sell programs"
            intro="You are buying certainty and a single move. The question is what that certainty costs."
            ids={["buy-before-you-sell"]}
            ctaLabel="Comparing a program fee against an open-market sale?"
          />

          <section id="matrix" className="mb-12 scroll-mt-24">
            <h2 className="mb-2 text-2xl font-bold text-foreground">Which option fits you</h2>
            <p className="mb-5 text-sm text-muted-foreground">
              Answer four questions. The matrix highlights the structures that fit and explains each factor behind
              the result. It is a starting point for a lender conversation, not a recommendation.
            </p>

            <Card className="mb-5">
              <CardContent className="space-y-5 p-5">
                {QUESTIONS.map((q) => (
                  <div key={q.key}>
                    <p className="mb-2 text-sm font-semibold text-foreground">{q.label}</p>
                    <div className="flex flex-wrap gap-2" role="group" aria-label={q.label}>
                      {q.options.map((o) => (
                        <Button
                          key={o.value}
                          size="sm"
                          variant={answers[q.key] === o.value ? "default" : "outline"}
                          aria-pressed={answers[q.key] === o.value}
                          onClick={() => setAnswer(q.key, o.value)}
                        >
                          {o.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <div className="space-y-4">
              {results.map((r, i) => {
                const o = opt(r.id);
                return (
                  <Card key={r.id} className={i === 0 ? "border-primary/40 bg-primary/5" : ""}>
                    <CardContent className="p-5">
                      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                        <h3 className="font-bold text-foreground">
                          {i === 0 ? "Best fit: " : `Also consider: `}
                          {o.name}
                        </h3>
                        <span className="text-xs font-semibold uppercase tracking-wide text-primary">
                          {o.doublePaymentRisk.split(" ")[0]} double-payment risk
                        </span>
                      </div>
                      <ul className="space-y-1.5">
                        {r.reasons.map((why) => (
                          <li key={why} className="flex gap-2 text-sm text-muted-foreground">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                            <span>{why}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <Card className="mt-5 border-primary/30 bg-primary/5">
              <CardContent className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-foreground">Get a second read on this from someone who knows your market.</p>
                <Button asChild onClick={() => handleCta("matrix", results[0]?.id ?? "none")}>
                  <Link to="/homebuying-support">
                    Talk through your options <ArrowRight className="ml-1 h-4 w-4" aria-hidden />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </section>

          <section id="timeline" className="mb-12 scroll-mt-24">
            <h2 className="mb-2 text-2xl font-bold text-foreground">30-60-90 day timeline</h2>
            <p className="mb-5 text-sm text-muted-foreground">
              Counted backward from the day you want keys to the new home.
            </p>
            <Accordion
              type="multiple"
              value={openStages}
              onValueChange={setOpenStages}
              className="rounded-xl border border-border bg-card px-4"
            >
              {MOVE_UP_TIMELINE.map((stage) => (
                <AccordionItem key={stage.id} value={stage.id} className="scroll-mt-24">
                  <AccordionTrigger className="text-left">
                    <span>
                      <h3 className="text-base font-bold text-foreground">{stage.heading}</h3>
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {stage.when} &middot; {stage.items.length} items
                      </span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="space-y-2 pb-2">
                      {stage.items.map((item) => (
                        <li key={item} className="flex gap-2 text-sm text-muted-foreground">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <section id="faq" className="mb-12 scroll-mt-24">
            <h2 className="mb-5 text-2xl font-bold text-foreground">Common questions</h2>
            <Accordion
              type="single"
              collapsible
              value={openFaq}
              onValueChange={handleFaqChange}
              className="rounded-xl border border-border bg-card px-4"
            >
              {BUY_BEFORE_SELLING_FAQS.map((f) => (
                <AccordionItem key={f.id} value={f.id}>
                  <AccordionTrigger className="text-left text-base font-semibold">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-muted-foreground">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <section className="mb-10">
            <h2 className="mb-3 text-xl font-bold text-foreground">Keep going</h2>
            <div className="flex flex-wrap gap-3 text-sm">
              <Link to="/tools/relocation-timing" className="text-primary hover:underline">
                Timing a cross-city move
              </Link>
              <Link to="/tools/home-buying-timeline" className="text-primary hover:underline">
                Home buying timeline
              </Link>
              <Link to="/tools/compare-lender-quotes" className="text-primary hover:underline">
                Compare lender quotes
              </Link>
              <Link to="/tools/homebuyer-readiness-score" className="text-primary hover:underline">
                Homebuyer readiness score
              </Link>
              <Link to="/tools/inherited-home-mortgage" className="text-primary hover:underline">
                Inherited a home with a mortgage
              </Link>
            </div>
          </section>

          <p className="text-xs text-muted-foreground">
            Educational information only &mdash; not legal, financial, or tax advice, and not an offer of credit or a
            commitment to lend. Loan availability, rates, fees, qualification standards, retirement plan loan rules,
            rent-back limits, and contingency acceptance vary by lender, plan, contract, and local market. Confirm
            every term with your lender, plan administrator, agent, tax professional, and where required a local
            attorney.
          </p>
        </div>
      </main>

      <RelatedTools currentPath="/tools/buy-before-selling" />
      <Footer />
    </div>
  );
};

export default BuyBeforeSelling;
